/**
 * PATH: apps/quote/src/components/builder/sheets/SummarySheet.tsx
 * PURPOSE: Display quote summary with totals, margins, and overview
 *
 * WHY:
 * - Excel quotes have a Summary sheet with totals and breakdown
 * - Users need a clear overview of quote financials
 * - Shows section totals, other charges, margins at a glance
 *
 * FLOW:
 *   Load quote → Calculate totals → Display breakdown → Highlight margins
 */

import { useMemo } from 'react'
import {
  TrendingUp,
  TrendingDown,
  Calculator,
  DollarSign,
  Layers,
  AlertTriangle,
  CheckCircle,
  Clock,
  Users,
} from 'lucide-react'
import { cn, formatCurrency } from '@/lib/utils'
import type { QuoteDetail } from '@/lib/quoteApi'

interface SummarySheetProps {
  quoteId: string
  quote: QuoteDetail
}

function toNumber(value: string | number | null | undefined): number {
  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    const n = parseFloat(value)
    return isNaN(n) ? 0 : n
  }
  return 0
}

function getMarginColor(margin: number) {
  if (margin >= 30) return 'text-emerald-600 dark:text-emerald-400'
  if (margin >= 25) return 'text-green-600 dark:text-green-400'
  if (margin >= 20) return 'text-lime-600 dark:text-lime-400'
  if (margin >= 15) return 'text-amber-600 dark:text-amber-400'
  if (margin >= 10) return 'text-orange-600 dark:text-orange-400'
  return 'text-red-600 dark:text-red-400'
}

function getMarginBg(margin: number) {
  if (margin >= 30) return 'bg-emerald-100 dark:bg-emerald-900/30'
  if (margin >= 25) return 'bg-green-100 dark:bg-green-900/30'
  if (margin >= 20) return 'bg-lime-100 dark:bg-lime-900/30'
  if (margin >= 15) return 'bg-amber-100 dark:bg-amber-900/30'
  if (margin >= 10) return 'bg-orange-100 dark:bg-orange-900/30'
  return 'bg-red-100 dark:bg-red-900/30'
}

function SummaryCard({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  trendLabel,
  className,
}: {
  title: string
  value: string
  subtitle?: string
  icon?: React.ElementType
  trend?: 'up' | 'down' | null
  trendLabel?: string
  className?: string
}) {
  return (
    <div className={cn('bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4', className)}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">{title}</p>
          <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{value}</p>
          {subtitle && (
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">{subtitle}</p>
          )}
        </div>
        {Icon && (
          <div className="p-2 bg-slate-100 dark:bg-slate-700 rounded-lg">
            <Icon className="w-5 h-5 text-slate-600 dark:text-slate-300" />
          </div>
        )}
      </div>
      {trend && (
        <div className={cn(
          'flex items-center gap-1 mt-3 text-sm',
          trend === 'up' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
        )}>
          {trend === 'up' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
          <span>{trendLabel}</span>
        </div>
      )}
    </div>
  )
}

function SectionBreakdownRow({
  title,
  items,
  labour,
  material,
  plant,
  subcon,
  total,
  isTotal = false,
}: {
  title: string
  items?: number
  labour: number
  material: number
  plant: number
  subcon: number
  total: number
  isTotal?: boolean
}) {
  return (
    <div className={cn(
      'grid grid-cols-[1fr_80px_100px_100px_90px_90px_120px] gap-2 px-4 py-3',
      isTotal
        ? 'bg-slate-100 dark:bg-slate-800 font-bold'
        : 'hover:bg-slate-50 dark:hover:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800'
    )}>
      <div className="flex items-center gap-2">
        {!isTotal && <Layers className="w-4 h-4 text-slate-400" />}
        <span className="text-sm text-slate-900 dark:text-white">{title}</span>
        {items !== undefined && (
          <span className="text-xs text-slate-400">({items} items)</span>
        )}
      </div>
      <div className="text-sm text-right text-blue-600 dark:text-blue-400 font-mono">
        {formatCurrency(labour)}
      </div>
      <div className="text-sm text-right text-amber-600 dark:text-amber-400 font-mono">
        {formatCurrency(material)}
      </div>
      <div className="text-sm text-right text-purple-600 dark:text-purple-400 font-mono">
        {formatCurrency(plant)}
      </div>
      <div className="text-sm text-right text-teal-600 dark:text-teal-400 font-mono">
        {formatCurrency(subcon)}
      </div>
      <div className={cn(
        'text-sm text-right font-mono font-semibold',
        isTotal ? 'text-slate-900 dark:text-white text-base' : 'text-slate-700 dark:text-slate-300'
      )}>
        {formatCurrency(total)}
      </div>
    </div>
  )
}

export default function SummarySheet({ quoteId, quote }: SummarySheetProps) {
  // Calculate summary data
  const summary = useMemo(() => {
    const sections = quote.sections || []
    
    // Section breakdown
    const sectionData = sections.map(section => {
      const items = section.items || []
      return {
        id: section.id,
        title: section.title,
        itemCount: items.length,
        labour: items.reduce((sum, i) => sum + toNumber(i.labour_chargeout), 0),
        material: items.reduce((sum, i) => sum + toNumber(i.material_chargeout), 0),
        plant: items.reduce((sum, i) => sum + toNumber(i.plant_chargeout), 0),
        subcon: items.reduce((sum, i) => sum + toNumber(i.subcon_chargeout), 0),
        total: toNumber(section.section_subtotal),
        manDays: items.reduce((sum, i) => sum + toNumber(i.labour_days), 0),
      }
    })

    // Totals
    const totals = {
      labour: sectionData.reduce((sum, s) => sum + s.labour, 0),
      material: sectionData.reduce((sum, s) => sum + s.material, 0),
      plant: sectionData.reduce((sum, s) => sum + s.plant, 0),
      subcon: sectionData.reduce((sum, s) => sum + s.subcon, 0),
      scopeSubtotal: toNumber(quote.scope_subtotal),
      otherCharges: toNumber(quote.other_charges_total),
      discount: toNumber(quote.discount_amount),
      totalExGst: toNumber(quote.total_excl_gst),
      totalIncGst: toNumber(quote.total_incl_gst),
      margin: toNumber(quote.overall_margin_percent),
      manDays: toNumber(quote.total_labour_days),
      itemCount: sections.reduce((sum, s) => sum + (s.items?.length || 0), 0),
    }

    return { sectionData, totals }
  }, [quote])

  const marginStatus = useMemo(() => {
    const margin = summary.totals.margin
    if (margin >= 25) return { status: 'good', icon: CheckCircle, label: 'Healthy margin' }
    if (margin >= 15) return { status: 'ok', icon: Clock, label: 'Acceptable margin' }
    return { status: 'low', icon: AlertTriangle, label: 'Low margin - review pricing' }
  }, [summary.totals.margin])

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">Quote Summary</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {quote.quote_number} • {quote.client_name || 'No client'} • {quote.project_name || 'Untitled project'}
          </p>
        </div>
        <div className={cn(
          'flex items-center gap-2 px-4 py-2 rounded-lg',
          marginStatus.status === 'good' && 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300',
          marginStatus.status === 'ok' && 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300',
          marginStatus.status === 'low' && 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
        )}>
          <marginStatus.icon className="w-5 h-5" />
          <span className="font-medium">{marginStatus.label}</span>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard
          title="Total Excl GST"
          value={formatCurrency(summary.totals.totalExGst)}
          subtitle={`Incl GST: ${formatCurrency(summary.totals.totalIncGst)}`}
          icon={DollarSign}
        />
        <SummaryCard
          title="Overall Margin"
          value={`${summary.totals.margin.toFixed(1)}%`}
          icon={Calculator}
          className={getMarginBg(summary.totals.margin)}
        />
        <SummaryCard
          title="Man Days"
          value={summary.totals.manDays.toFixed(1)}
          subtitle={`${summary.totals.itemCount} line items`}
          icon={Users}
        />
        <SummaryCard
          title="Sections"
          value={String(summary.sectionData.length)}
          subtitle={`${summary.totals.itemCount} total items`}
          icon={Layers}
        />
      </div>

      {/* Section Breakdown Table */}
      <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="px-4 py-3 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
          <h3 className="font-semibold text-slate-900 dark:text-white">Section Breakdown</h3>
        </div>
        
        {/* Header */}
        <div className="grid grid-cols-[1fr_80px_100px_100px_90px_90px_120px] gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-800 text-xs font-medium text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
          <div>Section</div>
          <div className="text-right text-blue-600 dark:text-blue-400">Labour</div>
          <div className="text-right text-amber-600 dark:text-amber-400">Materials</div>
          <div className="text-right text-purple-600 dark:text-purple-400">Plant</div>
          <div className="text-right text-teal-600 dark:text-teal-400">Subcon</div>
          <div className="text-right">Total</div>
        </div>

        {/* Section rows */}
        {summary.sectionData.map(section => (
          <SectionBreakdownRow
            key={section.id}
            title={section.title}
            items={section.itemCount}
            labour={section.labour}
            material={section.material}
            plant={section.plant}
            subcon={section.subcon}
            total={section.total}
          />
        ))}

        {/* Scope Subtotal */}
        <SectionBreakdownRow
          title="Scope Subtotal"
          labour={summary.totals.labour}
          material={summary.totals.material}
          plant={summary.totals.plant}
          subcon={summary.totals.subcon}
          total={summary.totals.scopeSubtotal}
          isTotal
        />
      </div>

      {/* Other Charges & Final Total */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white rounded-lg p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <p className="text-sm text-slate-400">Scope Subtotal</p>
            <p className="text-2xl font-bold">{formatCurrency(summary.totals.scopeSubtotal)}</p>
          </div>
          <div>
            <p className="text-sm text-slate-400">Other Charges</p>
            <p className="text-2xl font-bold text-emerald-400">{formatCurrency(summary.totals.otherCharges)}</p>
          </div>
          <div>
            <p className="text-sm text-slate-400">Discount</p>
            <p className="text-2xl font-bold text-red-400">
              {summary.totals.discount > 0 ? `-${formatCurrency(summary.totals.discount)}` : '-'}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-slate-400">Total Excl GST</p>
            <p className="text-3xl font-bold">{formatCurrency(summary.totals.totalExGst)}</p>
            <p className="text-sm text-slate-400 mt-1">
              Incl GST: <span className="text-white">{formatCurrency(summary.totals.totalIncGst)}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

